import { useState } from 'react';

import Select, { Option } from '@ingka/select';
import { translate } from '@repo/phrase';
import { type CancelOrderReasonCode, CancelOrderReasons } from '@repo/types/order';
import { Paragraph } from '@repo/ui/typography';

import { DialogModal } from '@/components/dialog-modal';
import { QueryError } from '@/components/query-error';

import { useAssetParams, useOrder } from '@/hooks/asset.hooks';

import { useCancelOrderMutation } from './asset-cancel-modal.hooks';

interface Props {
    visible: boolean;
    onClose: () => void;
}

export const AssetCancelModal = ({ visible, onClose }: Props) => {
    const { assetId } = useAssetParams();
    const [reasonCode, setReasonCode] = useState<CancelOrderReasonCode>();
    const { data, isError, error } = useOrder(assetId);
    const { mutate, isPending } = useCancelOrderMutation({
        orderData: data,
        reasonCode,
        closeModal: onClose,
    });

    if (isError && !data) {
        return <QueryError error={error} />;
    }

    const closeCancelDialog = () => {
        onClose();
        setReasonCode(undefined);
    };

    return (
        <DialogModal
            visible={visible}
            onClose={closeCancelDialog}
            title={translate('dialog_cancel_order_title', { number: assetId })}
            primaryButtonProps={{
                text: translate('dialog_modal_footer_yes'),
                onClick: mutate,
                loading: isPending,
                disabled: !reasonCode,
            }}
            secondaryButtonProps={{
                text: translate('dialog_modal_footer_no'),
                onClick: closeCancelDialog,
            }}
        >
            <Paragraph>
                {translate('dialog_cancel_order_body', {
                    number: assetId,
                })}
            </Paragraph>
            <Paragraph>{translate('dialog_cancel_order_body_reason')}</Paragraph>
            <Select
                id="cancel-reason-select"
                data-testid="cancel-order-reason-select"
                describedById={translate('order_cancel_reason_select')}
                value={reasonCode ?? ''}
                onChange={(e) => {
                    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
                    setReasonCode(e.target.value as CancelOrderReasonCode);
                }}
            >
                {CancelOrderReasons.map((reason) => (
                    <Option key={reason} value={reason} name={translate(`cancel_order_reason_${reason}`)} />
                ))}
            </Select>
        </DialogModal>
    );
};
